// src/encounter/services/use-previous-encounter-notes.ts
// GET /api/Patients/GetEncounterNotesDetails?Id=&patientId=&reasonForVisit= → the patient's earlier encounter notes for the
// same reason for visit (PROD-1523, `?x=previous-notes`). Flutter parity (scrips_msp1_flutter_shared origin/dev):
//   - called only on a FOLLOW-UP visit (encounter_screen.dart:159 `if (bloc?.isFollowUp) fetchPreviousEncounterNotes()`);
//   - Id = providerId() = the signed-in user's id, not practitionerId (patient_encounter_bloc.dart:1475, the same bare
//     providerId() the shell's Malaffi call documents), reasonForVisit = the appointment's reason CODE;
//   - 404 = no earlier notes, not an error (encounter_data_source_impl.dart:884).
// Scrips.Patient: PatientsController.GetEncounterNotesDetails → PatientEncounterResponse { enconterDetails, assessmentResponse, … }.
import { useQuery } from '@tanstack/react-query';
import { ApiError, get } from '@/shared/api/api-client';
import type { PreviousNotesInput, RawPreviousAssessment, RawPreviousEncounter } from '../lib/to-previous-notes';

/** The fields of PatientEncounterResponse this section reads (the wire key is spelled `enconterDetails`). */
export interface PreviousEncounterNotesResponse {
  enconterDetails?: RawPreviousEncounter[] | null;
  assessmentResponse?: Array<{
    encounter?: string | null;
    assessmentPlanCondition?: Array<{
      conditionCode?: { display?: string | null } | null;
      externalCode?: { display?: string | null } | null;
    } | null> | null;
  } | null> | null;
}

export interface PreviousEncounterNotesParams {
  /** Flutter providerId(): the signed-in user's id. */
  userId: string | null | undefined;
  patientId: string | null | undefined;
  /** The appointment's reason-for-visit CODE (the encounter's `reasonForVisit`). */
  reasonForVisitCode: string | null | undefined;
  /** Only a follow-up visit asks (Flutter isFollowUp). */
  isFollowUp: boolean;
  /** The `?x=previous-notes` switch: off = no request. */
  enabled: boolean;
}

/** The diagnosis line reads the condition's display (the API sends no `description`). */
export function toPreviousNotesInput(res: PreviousEncounterNotesResponse | null | undefined): Pick<PreviousNotesInput, 'encounters' | 'assessments'> {
  const assessments: RawPreviousAssessment[] = (res?.assessmentResponse ?? []).flatMap((a) =>
    a
      ? [{
          encounter: a.encounter ?? null,
          assessmentPlanCondition: (a.assessmentPlanCondition ?? []).map((c) => ({
            description: c?.conditionCode?.display ?? c?.externalCode?.display ?? null,
          })),
        }]
      : [],
  );
  return { encounters: res?.enconterDetails ?? [], assessments };
}

export function previousEncounterNotesUrl(userId: string, patientId: string, reasonForVisitCode: string): string {
  const q = new URLSearchParams({ Id: userId, patientId, reasonForVisit: reasonForVisitCode });
  return `/api/Patients/GetEncounterNotesDetails?${q.toString()}`;
}

export function usePreviousEncounterNotes({ userId, patientId, reasonForVisitCode, isFollowUp, enabled }: PreviousEncounterNotesParams) {
  return useQuery<PreviousEncounterNotesResponse | null>({
    queryKey: ['previous-encounter-notes', userId, patientId, reasonForVisitCode],
    enabled: enabled && isFollowUp && !!userId && !!patientId && !!reasonForVisitCode,
    queryFn: ({ signal }) => fetchPreviousEncounterNotes(userId as string, patientId as string, reasonForVisitCode as string, signal),
  });
}

/** 404 = the patient has no earlier notes for this reason (Flutter returns null); any other failure stays an error. */
export async function fetchPreviousEncounterNotes(
  userId: string,
  patientId: string,
  reasonForVisitCode: string,
  signal?: AbortSignal,
): Promise<PreviousEncounterNotesResponse | null> {
  try {
    return await get<PreviousEncounterNotesResponse>(previousEncounterNotesUrl(userId, patientId, reasonForVisitCode), { signal });
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}
